import { CURRENT_MODEL_VERSION } from '@shared/constants';
import { getSeasonIdFromGameID, isSeasonYearMatchUID, toSeasonWeekID } from '@shared/helpers';
import {
  AccumulatedTeamCategoriesByGameStats,
  AccumulatedTeamStats,
  HeadToHeadFirebaseCollection,
  HeadToHeadID,
  ICategoryRanking,
  IEnhancedTeamStats,
  IGame,
  IGamePrediction,
  IGameStats,
  ITeamDetails,
  ITeamsOnByeWeek,
  ITeamStandings,
  SeasonID,
  SeasonTeamAbrvID,
  WeekID,
} from '@shared/types';
import { fetchHeadToHeadGameResults } from 'api/admin/AdminHeadToHeadAPI';
import { fetchCategoryRankingsForWeek } from 'api/CategoryRankingsAPI';
import { fetchGamePredictionsBySeasonID } from 'api/PredictionsAPI';
import { fetchMatchResults, fetchMatchSchedule } from 'api/ScheduleAPI';
import { fetchStatisticsForWeek } from 'api/StatsAPI';
import { fetchTeamDetails, fetchTeamMatchResults, fetchTeamsOnByeWeek } from 'api/TeamsAPI';
import { fetchTeamStandings } from 'api/TeamStandingsAPI';
import {
  fetchTeamCumulativeStatsBySeasonID,
  fetchTeamRanksBySeasonID,
  fetchTeamWeeklyStatsBySeasonID,
} from 'api/TeamStatsAPI';
import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { Cls, Emo, Emojis } from 'app/constants';
import { IDBLoaderFunction, RankingsPayload, Stores, SyncIDBStores, SyncStoreProps } from 'types';
import {
  addData,
  awaitForReadyState,
  createStore,
  deleteStore,
  getDataByIndex,
  getStoreData,
  getStoreDataByKey,
  updateData,
} from './IndexDb';

const CONSOLE_ON = getConsoleState(ConsoleID.IndexedDBInterface);

type DataAnyFn = (data: any) => void;

// #region CREATE / DELETE INDEX DB
export const createNewStore = async (storeName: string, keyPath: string, newVersion: number, callbackFn: DataAnyFn) => {
  await createStore(storeName, keyPath, newVersion).then((data) => {
    if (CONSOLE_ON) console.log(`New Store ${storeName} has been created:`);
    callbackFn(data);
  });
};

export const deleteStoreFromIDB = async (storeName: string, newVersion: number, callbackFn: DataAnyFn) => {
  await deleteStore(storeName, newVersion).then((data) => {
    if (CONSOLE_ON) console.log(`Store ${storeName} has been deleted:`);
    callbackFn(data);
  });
};
// #endregion

export const createDefaultIDBSyncStore = (id: Stores): SyncStoreProps => ({
  id,
  lastUpdatedOn: 0,
  timestamp: '',
});

export const loadSyncStatusFromDB = async ({ storeName, callbackFn }: IDBLoaderFunction): Promise<void> => {
  await getStoreData<SyncStoreProps>(storeName).then((data) => {
    // Convert array to object structure
    const syncStores: Partial<SyncIDBStores> = {};
    let feLastUpdatedOn: number = 0;
    let clientLastUpdatedOn: string = '';

    // find the oldest updated time in the IDB
    let lowestClientLastUpdatedOn = `${new Date().toISOString()}`;
    let lowestFeLastUpdatedOn = Date.now();
    data.forEach((storeCollection: SyncStoreProps) => {
      syncStores[storeCollection.id as keyof SyncIDBStores] = storeCollection;
      lowestFeLastUpdatedOn = Math.min(lowestFeLastUpdatedOn, storeCollection.lastUpdatedOn);
      lowestClientLastUpdatedOn =
        storeCollection.timestamp < lowestClientLastUpdatedOn ? storeCollection.timestamp : lowestClientLastUpdatedOn;
    });

    const updatedOn = new Date(lowestClientLastUpdatedOn).getTime();
    const lowestValue = Math.min(updatedOn, lowestFeLastUpdatedOn);

    // data.forEach((storeCollection: SyncStoreProps) => {
    //   syncStores[storeCollection.id as keyof SyncIDBStores] = storeCollection;
    //   const timestamp = storeCollection.timestamp || new Date().getTime().toString();
    //   feLastUpdatedOn = Math.max(feLastUpdatedOn, storeCollection.lastUpdatedOn);
    //   clientLastUpdatedOn = timestamp > clientLastUpdatedOn ? timestamp : clientLastUpdatedOn;
    // });

    feLastUpdatedOn = lowestValue;
    clientLastUpdatedOn = new Date(lowestValue).toISOString();
    const msg = `feLastUpdatedOn %c ${feLastUpdatedOn} %c clientLastUpdatedOn %c ${clientLastUpdatedOn} %c`;
    console.log(`${Emo.hourglass} ${msg}`, Cls.Info, '', Cls.Val, '');

    callbackFn({ syncStores, feLastUpdatedOn, clientLastUpdatedOn }); // ✅ Pass the transformed object
  });
};

/**
 * Writes records to the specified IndexedDB store.
 *
 * @param {string} storeName - Name of the IDB store
 * @param {T[]} recs - Records to write
 * @param {DataAnyFn} callbackFn - Callback function to execute after writing
 *
 * @return {Promise<void>} Promise that resolves when writing is complete
 */
export const writeRecordsInIDB = async <T>(storeName: string, recs: T[], callbackFn: DataAnyFn): Promise<void> => {
  await persistRecordsInIDBDatabase(storeName, recs, (res) => {
    const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with records from %c Firebase %c`;
    if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.Loc, '');
    callbackFn(res);
  });
};

// #region LOADING RECORDS
export const loadGameScheduleFromDB = async ({
  storeName,
  callbackFn,
  year,
  shouldFetch,
}: IDBLoaderFunction): Promise<void> => {
  await getStoreData<IGame>(storeName).then(async (data) => {
    const noData = !data || data.length === 0;

    const scheduleForYear = noData ? [] : year === 'full' ? data : data.filter((game) => game.season === year);

    if (scheduleForYear.length === 0 || shouldFetch) {
      if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
      const gameData = await fetchMatchSchedule(year);
      const dataMsg1 = `%c FETCHED %c %c ${gameData.length} %c schedule`;
      const dataMsg2 = `records for year %c ${year} %c from %c Firebase %c`;
      const msg = `${Emojis.eyes} ${dataMsg1} ${dataMsg2}`;
      if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Val, '', Cls.Info, '', Cls.Loc, '');
      if (gameData.length > 0) {
        // await persistRecordsInIDBDatabase(storeName, gameData, (res) => {
        //   const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with records from %c Firebase %c`;
        //   if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.Loc, '');
        //   callbackFn(res);
        // });
        await writeRecordsInIDB(storeName, gameData, callbackFn);
      } else {
        callbackFn(data);
      }
    } else {
      callbackFn(scheduleForYear);
    }
  });
};

export const loadGameResultsFromDB = async ({
  storeName,
  callbackFn,
  year,
  shouldFetch,
}: IDBLoaderFunction): Promise<void> => {
  await getStoreData<IGameStats>(storeName).then(async (data) => {
    const filteredGameData = data.filter((game) => getSeasonIdFromGameID(game.gameID) === year);
    if (!filteredGameData || filteredGameData.length === 0 || shouldFetch) {
      if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
      const gameData = await fetchMatchResults(year);
      const msg = `${Emojis.eyes} %c FETCHED %c %c ${gameData.length} %c records from %c Firebase %c`;
      if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.ValLg, '', Cls.Loc, '');
      if (gameData.length > 0) {
        await persistRecordsInIDBDatabase(storeName, gameData, (res) => {
          const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with records from %c Firebase %c`;
          if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.Loc, '');
          callbackFn(res);
        });
      } else {
        callbackFn(gameData);
      }
    } else {
      callbackFn(data);
    }
  });
};

export const loadTeamResultsFromDB = async ({
  storeName,
  callbackFn,
  year,
  shouldFetch,
}: IDBLoaderFunction): Promise<void> => {
  await getStoreData<IEnhancedTeamStats>(storeName).then(async (data) => {
    const filteredGameData = data.filter((game) => isSeasonYearMatchUID(game.uID, year));
    if (!filteredGameData || filteredGameData.length === 0 || shouldFetch) {
      if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
      const gameData = await fetchTeamMatchResults(year);
      const msgIntro = `${Emojis.eyes} %c FETCHED %c %c year ${year}`;
      const msg = `${msgIntro} ${gameData.length} %c records from %c Firebase %c`;
      if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.ValLg, '', Cls.Loc, '');

      if (gameData.length > 0) {
        await persistRecordsInIDBDatabase(storeName, gameData, (res) => {
          const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with records from %c Firebase %c`;
          if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.Loc, '');
          callbackFn(res);
        });
      } else {
        callbackFn(data);
      }
    } else {
      callbackFn(data);
    }
  });
};

export const loadTeamDetailsFromDB = async ({
  storeName, callbackFn, year, shouldFetch,
}: IDBLoaderFunction): Promise<void> => {
  await getStoreData<ITeamDetails>(storeName).then(async (data) => {
    const hasSeason = data?.length > 0 && (year === 'full' || data.every((team) =>
      Object.prototype.hasOwnProperty.call(team.scheduleByYear || {}, year)));
    if (!hasSeason || shouldFetch) {
      if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
      let gameData = await fetchTeamDetails();
      const msg = `${Emojis.eyes} %c FETCHED %c %c ${gameData.length} %c records from %c Firebase %c`;
      if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.ValLg, '', Cls.Loc, '');

      if (gameData.length > 0) {
        gameData = gameData.map((team) => ({ ...team, teamID: team.teamID.padStart(4, '0') }));
        gameData.sort((a, b) => parseInt(a.teamID) - parseInt(b.teamID));
      }

      await persistRecordsInIDBDatabase(storeName, gameData, async (teamDetails: ITeamDetails[]) => {
        const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with records from %c Firebase %c`;
        if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.Loc, '');
        const updateTeamDetails = teamDetails.map((team) => ({ ...team, teamID: `${parseInt(team.teamID)}` }));
        callbackFn(updateTeamDetails);
        // const teamsOnByeWeek = getAllTeamsAllAvailableSeasonsByeWeeks(teamDetails);
        // const details = [...teamDetails];
        // const msg1 = `${Emojis.eyes} %c UPDATING %c %c BYE WEEKS %c`;
        // if (CONSOLE_ON) console.log(msg1, '', Cls.Act, '', Cls.Loc, '');
        // await persistRecordsInIDBDatabase(Stores.TeamsOnByeWeek, teamsOnByeWeek, () => {
        //   callbackFn(details);
        // });
      });
    } else {
      const updateTeamDetails = data.map((team) => ({ ...team, teamID: `${parseInt(team.teamID)}` }));
      callbackFn(updateTeamDetails);
    }
  });
};

/**
 * Loads cached bye-week rows when the requested season exists. Otherwise fetches
 * that season and persists it before returning exactly one response. Full-store
 * callers retain their all-season read; shouldFetch still forces a source fetch.
 *
 * @param {Stores} storeName - name of the IDB store to load data from
 * @param {Function} callbackFn - function to call with the loaded data
 * @param {boolean} shouldFetch - whether to force fetch data from Firebase
 *
 * @return {Promise<void>} - Promise that resolves when the data is loaded and callback is executed
 */
export const loadTeamsOnByeWeekFromDB = async ({
  storeName,
  callbackFn,
  shouldFetch,
  year,
}: IDBLoaderFunction): Promise<void> => {
  await getStoreData<ITeamsOnByeWeek>(storeName).then(async (data) => {
    const noData = !data?.some((week) => year === 'full' || week.year === year);
    if (noData || shouldFetch) {
      if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
      const byeWeeks: ITeamsOnByeWeek[] = await fetchTeamsOnByeWeek(year === 'full' ? undefined : year);
      const msg = `${Emojis.eyes} %c FETCHED %c %c ${byeWeeks.length} %c records from %c Firebase %c`;
      if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.ValLg, '', Cls.Loc, '');
      await persistRecordsInIDBDatabase(storeName, byeWeeks, (res) => {
        const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with records from %c Firebase %c`;
        if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.Loc, '');
        callbackFn(res);
      });
    } else {
      callbackFn(data);
    }
  });
};

/**
 * Loads HeadToHead records from IndexedDB.
 * Fetches from Firebase if IDB is empty or shouldFetch=true.
 *
 * @param {Stores} storeName - Name of the IDB store (Stores.HeadToHead)
 * @param {Function} callbackFn - Callback with loaded data
 * @param {boolean} shouldFetch - Whether to force fetch from Firebase
 *
 * @return {Promise<void>}
 */
export const loadHeadToHeadFromDB = async ({
  storeName,
  callbackFn,
  shouldFetch,
}: IDBLoaderFunction): Promise<void> => {
  const msg = `${Emojis.eyes} %c LOADING %c %c ${storeName} %c from %c IndexedDB %c`;
  if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.Loc, '');

  try {
    // Step 1: Try loading from IDB
    const idbData = await getStoreData<HeadToHeadFirebaseCollection>(storeName);

    // Admin generation can populate a subset before the first full collection load.
    // Partial writes preserve the marker; only a full load establishes freshness.
    const syncStatus = await getStoreDataByKey<SyncStoreProps>(Stores.SyncStatus, storeName);
    const hasFullLoad = (syncStatus?.lastUpdatedOn ?? 0) > 0;
    if (idbData.length === 0 || shouldFetch || !hasFullLoad) {
      const fbMsg = `${Emojis.rocket} %c FETCHING %c from %c Firebase %c (IDB empty=${idbData.length === 0})`;
      if (CONSOLE_ON) console.log(fbMsg, Cls.Act, '', Cls.Loc, '');

      // Fetch from Firebase
      const fbData: Record<HeadToHeadID, HeadToHeadFirebaseCollection> = await fetchHeadToHeadGameResults();

      // Convert to array for IDB storage (preserve keys as headToHeadID property)
      const dataArray = Object.entries(fbData).map(([headToHeadID, data]) => ({
        ...data,
        headToHeadID,
      }));

      // Write to IDB
      await writeRecordsInIDB(storeName, dataArray, (result: HeadToHeadFirebaseCollection[]) => {
        const writeMsg = `${Emojis.checkBox} %c WROTE %c ${result.length} records to %c ${storeName} %c`;
        if (CONSOLE_ON) console.log(writeMsg, Cls.Act, '', Cls.Loc, '');
      });

      // Execute callback with fresh data
      callbackFn(dataArray);
    } else {
      // Step 3: Use IDB data
      const cacheMsg = `${Emojis.checkBox} %c LOADED %c ${idbData.length} records from %c IDB cache %c`;
      if (CONSOLE_ON) console.log(cacheMsg, Cls.Act, '', Cls.Loc, '');
      callbackFn(idbData);
    }
  } catch (error) {
    const errMsg = `${Emojis.ex} %c ERROR %c loading ${storeName}: ${error}`;
    console.error(errMsg, Cls.Cri, '');
    throw error;
  }
};

export const loadSeasonStandingsFromDB = async ({
  storeName,
  callbackFn,
  year,
  shouldFetch,
}: IDBLoaderFunction): Promise<void> => {
  await getStoreData<ITeamStandings>(storeName).then(async (data) => {
    const noData = !data || data.length === 0;
    const seasonStandings = noData ? [] : data.filter((t) => isSeasonYearMatchUID(t.uID, year));

    if (seasonStandings.length === 0 || shouldFetch) {
      if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
      const gameData = await fetchTeamStandings(year);
      const msg = `${Emojis.eyes} %c FETCHED %c %c ${gameData.length} %c records from %c Firebase %c`;
      if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.ValLg, '', Cls.Loc, '');

      if (gameData.length > 0) {
        await persistRecordsInIDBDatabase(storeName, gameData, (res) => {
          const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with records from %c Firebase %c`;
          if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.Loc, '');
          callbackFn(res);
        });
      } else {
        callbackFn([]);
      }
    } else {
      callbackFn(data);
    }
  });
};

const fetchStatsFromFirebase = async (weekNum: number, yearID: SeasonID): Promise<RankingsPayload> => {
  const weekID: WeekID = `Week${weekNum}`;
  const seasonWeekID = toSeasonWeekID(yearID, weekID);
  const rankingsPayload: RankingsPayload = await fetchStatisticsForWeek(weekID, yearID, seasonWeekID);
  const msg = `${Emojis.eyes} %c FETCHED %c rankingsPayload record from %c Firebase %c`;
  if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '');
  return rankingsPayload;
};

const loadAllWeeksFromFirebase = async (year: SeasonID): Promise<RankingsPayload[]> => {
  const allWeeks: RankingsPayload[] = [];
  for (let weekNum = 1; weekNum <= 18; weekNum++) {
    const rankingsPayload = await fetchStatsFromFirebase(weekNum, year);
    if (rankingsPayload) allWeeks.push(rankingsPayload);
  }
  return allWeeks;
};

export const loadWeekRankFromDB = async ({
  storeName,
  callbackFn,
  year,
  shouldFetch,
  weekID,
}: IDBLoaderFunction): Promise<void> => {
  if (!weekID) {
    await getStoreData<RankingsPayload>(storeName).then(async (data) => {
      const noData = !data || data.length === 0;
      const weekRanksByYear = noData
        ? []
        : data.filter((r) => r?.seasonWeekID && r.seasonWeekID.startsWith(year) && r.calculated);

      if (weekRanksByYear.length === 0 || shouldFetch) {
        if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
        const gameData = await loadAllWeeksFromFirebase(year);
        if (gameData.length > 0) {
          await persistRecordsInIDBDatabase(storeName, gameData, (res) => {
            const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with records from %c Firebase %c`;
            if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.Loc, '');
            callbackFn(res);
          });
        } else {
          callbackFn([]);
        }
      } else {
        callbackFn(weekRanksByYear);
      }
    });
  } else {
    const seasonWeekID = toSeasonWeekID(year, weekID);
    await getStoreDataByKey<RankingsPayload>(storeName, seasonWeekID).then(async (data) => {
      const noData = !data;
      // #region Debug logging
      const keys = data ? Object.keys(data) : [];
      if (keys.length > 0) {
        const msg = `%c LOADED %c FROM %c IDB:${storeName} %c %c ${keys.toString()} %c`;
        if (CONSOLE_ON) console.log(`${Emojis.checkBox} ${msg}`, Cls.Act, '', Cls.Army, '', Cls.Val, '');
      }
      // #endregion
      if (noData || shouldFetch) {
        if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
        const gameData = await loadAllWeeksFromFirebase(year);
        if (gameData.length > 0) {
          await persistRecordsInIDBDatabase(storeName, gameData, (res) => {
            const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with records from %c Firebase %c`;
            if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.Loc, '');
            callbackFn(res);
          });
        } else {
          callbackFn([]);
        }
      } else {
        callbackFn(data);
      }
    });
  }
};

export const loadCategoryRankingsFromDB = async ({
  storeName,
  callbackFn,
  year,
  shouldFetch,
  weekID,
}: IDBLoaderFunction): Promise<void> => {
  if (!weekID) {
    if (CONSOLE_ON) console.warn(`${Emojis.warning} loadCategoryRankingsFromDB requires weekID`);
    callbackFn([]);
    return;
  }

  const seasonWeekID = toSeasonWeekID(year, weekID);

  await getDataByIndex<ICategoryRanking>(storeName, 'seasonWeekID', seasonWeekID).then(async (data) => {
    const noData = !data || data.length === 0;

    if (noData || shouldFetch) {
      if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
      const rankings = await fetchCategoryRankingsForWeek(seasonWeekID);

      if (rankings.length > 0) {
        await persistRecordsInIDBDatabase(storeName, rankings, (res) => {
          const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with records from %c Firebase %c`;
          if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.Loc, '');
          callbackFn(res);
        });
      } else {
        callbackFn([]);
      }
    } else {
      callbackFn(data);
    }
  });
};

export const loadTeamWeeklyStatsFromDB = async ({
  storeName,
  callbackFn,
  year,
  shouldFetch,
}: IDBLoaderFunction): Promise<void> => {
  await getStoreData<AccumulatedTeamStats & { seasonTeamAbrvID: SeasonTeamAbrvID }>(storeName).then(
    async (dataArray) => {
      const filteredData = dataArray.filter((stat) => stat.seasonTeamAbrvID?.startsWith(year));
      const noData = filteredData.length === 0;

      if (noData || shouldFetch) {
        if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
        const stats = await fetchTeamWeeklyStatsBySeasonID(year);
        const statsArray = Object.entries(stats).map(([key, value]) => ({
          ...value,
          seasonTeamAbrvID: key as SeasonTeamAbrvID,
        }));

        if (statsArray.length > 0) {
          await persistRecordsInIDBDatabase(storeName, statsArray, (res) => {
            const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with %c ${statsArray.length} %c records`;
            if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.ValLg, '');
            const statsRecord: AccumulatedTeamCategoriesByGameStats = {};
            res.forEach((stat: AccumulatedTeamStats & { seasonTeamAbrvID: SeasonTeamAbrvID }) => {
              statsRecord[stat.seasonTeamAbrvID] = stat;
            });
            callbackFn(statsRecord);
          });
        } else {
          callbackFn({});
        }
      } else {
        const statsRecord: AccumulatedTeamCategoriesByGameStats = {};
        filteredData.forEach((stat) => {
          statsRecord[stat.seasonTeamAbrvID] = stat;
        });
        callbackFn(statsRecord);
      }
    },
  );
};

export const loadTeamCumulativeStatsFromDB = async ({
  storeName,
  callbackFn,
  year,
  shouldFetch,
}: IDBLoaderFunction): Promise<void> => {
  await getStoreData<AccumulatedTeamStats & { seasonTeamAbrvID: SeasonTeamAbrvID }>(storeName).then(
    async (dataArray) => {
      const filteredData = dataArray.filter((stat) => stat.seasonTeamAbrvID?.startsWith(year));
      const noData = filteredData.length === 0;

      if (noData || shouldFetch) {
        if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
        const stats = await fetchTeamCumulativeStatsBySeasonID(year);
        const statsArray = Object.entries(stats).map(([key, value]) => ({
          ...value,
          seasonTeamAbrvID: key as SeasonTeamAbrvID,
        }));

        if (statsArray.length > 0) {
          await persistRecordsInIDBDatabase(storeName, statsArray, (res) => {
            const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with %c ${statsArray.length} %c records`;
            if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.ValLg, '');
            const statsRecord: AccumulatedTeamCategoriesByGameStats = {};
            res.forEach((stat: AccumulatedTeamStats & { seasonTeamAbrvID: SeasonTeamAbrvID }) => {
              statsRecord[stat.seasonTeamAbrvID] = stat;
            });
            callbackFn(statsRecord);
          });
        } else {
          callbackFn({});
        }
      } else {
        const statsRecord: AccumulatedTeamCategoriesByGameStats = {};
        filteredData.forEach((stat) => {
          statsRecord[stat.seasonTeamAbrvID] = stat;
        });
        callbackFn(statsRecord);
      }
    },
  );
};

export const loadTeamRanksFromDB = async ({
  storeName,
  callbackFn,
  year,
  shouldFetch,
}: IDBLoaderFunction): Promise<void> => {
  await getStoreData<AccumulatedTeamStats & { seasonTeamAbrvID: SeasonTeamAbrvID }>(storeName).then(
    async (dataArray) => {
      const filteredData = dataArray.filter((stat) => stat.seasonTeamAbrvID?.startsWith(year));
      const noData = filteredData.length === 0;

      if (noData || shouldFetch) {
        if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
        const ranks = await fetchTeamRanksBySeasonID(year);
        const ranksArray = Object.entries(ranks).map(([key, value]) => ({
          ...value,
          seasonTeamAbrvID: key as SeasonTeamAbrvID,
        }));

        if (ranksArray.length > 0) {
          await persistRecordsInIDBDatabase(storeName, ranksArray, (res) => {
            const msg = `${Emojis.eyes} %c UPDATED %c %c ${storeName} %c with %c ${ranksArray.length} %c records`;
            if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '', Cls.ValLg, '');
            const ranksRecord: AccumulatedTeamCategoriesByGameStats = {};
            res.forEach((rank: AccumulatedTeamStats & { seasonTeamAbrvID: SeasonTeamAbrvID }) => {
              ranksRecord[rank.seasonTeamAbrvID] = rank;
            });
            callbackFn(ranksRecord);
          });
        } else {
          callbackFn({});
        }
      } else {
        const ranksRecord: AccumulatedTeamCategoriesByGameStats = {};
        filteredData.forEach((rank) => {
          ranksRecord[rank.seasonTeamAbrvID] = rank;
        });
        callbackFn(ranksRecord);
      }
    },
  );
};

export const loadPredictionsFromDB = async ({
  storeName,
  callbackFn,
  year,
  shouldFetch,
  modelVersion,
}: IDBLoaderFunction): Promise<void> => {
  const versionToUse = modelVersion || CURRENT_MODEL_VERSION;

  await getStoreData<IGamePrediction>(storeName).then(async (data) => {
    const filteredData = data.filter((p) => p.seasonID === year && p.modelVersion === versionToUse);

    if (filteredData.length === 0 || shouldFetch) {
      if (CONSOLE_ON) console.log(`${Emojis.warning} NO DATA IN %c ${storeName} %c`, Cls.Loc, '');
      const predictionsMap = await fetchGamePredictionsBySeasonID(year, versionToUse);
      const predictions = Object.values(predictionsMap);
      const msg = `${Emojis.eyes} %c FETCHED %c %c ${predictions.length} %c records from %c Firebase %c`;
      if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.ValLg, '', Cls.Loc, '');

      if (predictions.length > 0) {
        await persistRecordsInIDBDatabase(storeName, predictions, (res) => {
          callbackFn(res);
        });
      } else {
        callbackFn([]);
      }
    } else {
      callbackFn(filteredData);
    }
  });
};

/**
 * Load season rankings from the IDB
 *
 * @param {string} storeName - name of the IDB store to load data from
 * @param {Function} callbackFn - callback function to handle the loaded data
 *
 * @return {Promise<void>} - Promise that resolves when the data is loaded and callback is executed
 *
 * @deprecated - This function is deprecated, use `loadWeekRankFromDB` instead
 */
export const loadSeasonRankFromDB = async (storeName: string, callbackFn: (data: any) => void): Promise<void> => {
  await getStoreData<RankingsPayload>(storeName).then((data) => {
    callbackFn(data);
  });
};
// #endregion

// #region UPDATING / ADDING RECORDS

export const addRecordsToIDBDatabase = async (
  storeName: string,
  data: any,
  callbackFn?: (data: any) => void,
): Promise<void> => {
  const readyState = await awaitForReadyState();
  if (!readyState) {
    if (CONSOLE_ON) console.error('IDB request not ready');
    return;
  }

  try {
    const res = await addData(storeName, data);
    callbackFn?.(res);
  } catch (err: unknown) {
    if (err instanceof Error) {
      if (CONSOLE_ON) console.error(err.message);
    } else {
      if (CONSOLE_ON) console.error('Something went wrong');
    }
  }
};

/** Rejects failures so worker loaders cannot acknowledge unsuccessful persistence. */
export const persistRecordsInIDBDatabase = async <T>(
  storeName: string, data: T, callbackFn?: (data: T) => unknown
): Promise<void> => {
  const result = await updateData(storeName, data);
  await callbackFn?.(result);
};
// #endregion

/**
 * Compatibility for legacy fire-and-forget callers. New awaited loaders must use
 * persistRecordsInIDBDatabase so a handled error cannot be mistaken for success.
 */
export const updateRecordsInIDBDatabase = async <T>(
  storeName: string, data: T, callbackFn?: (data: T) => unknown
): Promise<void> => {
  try {
    await persistRecordsInIDBDatabase(storeName, data, callbackFn);
  } catch (error) {
    console.error(`Unable to persist ${storeName} in IndexedDB`, error);
  }
};
